import { useEffect } from 'react';

import { useAppSyncEvents } from '@/shared/hooks/use-appsync-events/use-app-sync-events';
import {
  TIdentObjectTypeEnum,
  TIdentOperationTypeEnum,
} from '@/shared/hooks/use-ident-app-sync-events/use-ident-app-sync-events.types';
import { useInvalidateIdentTags } from '@/shared/hooks/use-invalidate-ident-tags/use-invalidate-ident-tags';
import { auditApi } from '@/shared/redux/api/audit/audit-api';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import { setBackgroundMode } from '@/shared/redux/background-refetch/background-refetch.slice';
import { useAppDispatch, useAppSelector } from '@/shared/redux/hooks/hooks';
import { snakeToCamelCase } from '@/shared/utils/transform';

export const useIdentAppSyncEvents = () => {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const { data } = useReadTenantQuery({}, { skip: !user });
  const { invalidateIdentTags } = useInvalidateIdentTags();
  const { connectAndSubscribe, disconnect } = useAppSyncEvents({});

  const tenantId = data?.readTenant?.tenantId;
  const webSocketChannel = `default/${tenantId}/ident`;

  const onMessageReceived = (data: any) => {
    const event = snakeToCamelCase(data?.event);
    const identObjectType = event?.identObjectType as TIdentObjectTypeEnum;
    const identObjectId = event?.identObjectId;
    const operationType = event?.operationType as TIdentOperationTypeEnum;

    if (!identObjectType || !identObjectId || !operationType) {
      console.warn('Invalid ident event data', data);
      return;
    }

    dispatch(setBackgroundMode());

    invalidateIdentTags({
      identObjectType,
      identObjectId,
      operationType,
      payload: event?.identObject,
    });

    dispatch(auditApi.util.invalidateTags([{ type: 'AuditLogs', id: 'LIST' }]));
  };

  // Keyed on the tenant id, not the query result: a tenant update invalidates
  // `Tenant`, and re-running on the refetched object would drop this channel
  // (and the assets and jobs ones, which share the query) on every edit.
  useEffect(() => {
    if (tenantId) {
      void connectAndSubscribe(webSocketChannel, onMessageReceived);
    }
    return disconnect;
  }, [tenantId]);
};
